import { useCallback } from 'react';
import { List } from 'immutable';
import {
    transferAttendedCall,
    transferBlindCall,
} from 'flow/actions/phone-actions';
import { selectExtension } from 'flow/selectors/ExtensionSelectors';
import { getKeyboardShortcuts } from 'flow/selectors/keyboard-shortcuts-selectors';
import {
    ContactKey,
    ExtensionKey,
    ThirdPartyContactKey,
} from 'flow/types/EntityKeys';
import { Extension } from 'flow/types/Extension';
import { KeyboardShortcut } from 'flow/types/KeyboardShortcut';
import { AppDispatch } from 'flow/types/Redux';
import { State } from 'flow/types/State';
import { TypedMap } from 'flow/types/TypedMap';
import {
    getFixedNumberFromExtension,
    getMobileNumberFromExtension,
} from 'flow/utils/extension-utils';
import { isMacOs } from 'flow/utils/os-utils-flow';
import { normalizeShortcut } from 'flow/utils/shortcut-utils';
import { useAppDispatch } from './useRedux';

interface Props {
    entityKey?: ExtensionKey | ContactKey | ThirdPartyContactKey;
    setTransferSelectionDialogOpen?: (
        transferSelectionDialogOpen: boolean,
    ) => void;
}

interface ActionProps extends Props {
    extension?: TypedMap<Extension>;
}

type ShortcutAction = (
    dispatch: AppDispatch,
    event: KeyboardEvent,
    props: ActionProps,
) => void;

const checkShortcutMatch = (event: KeyboardEvent, shortcut: string) => {
    const normalizedShortcut = normalizeShortcut(shortcut);
    const keys = normalizedShortcut
        ?.split('+')
        .map((key) => key.trim().toLowerCase());
    const keyPressed = event.key.toLowerCase();

    const isModifierKey = (key: string) => {
        switch (key) {
            case 'cmdorctrl':
            case 'cmd':
            case 'ctrl':
            case 'control':
                return event.metaKey || event.ctrlKey;
            case 'shift':
                return event.shiftKey;
            case 'alt':
                return event.altKey;
            case 'altgraph':
                return event.getModifierState('AltGraph');
            default:
                return false;
        }
    };

    const hasAllModifiers = keys
        ?.filter((key) => key !== keyPressed)
        .every(isModifierKey);

    return hasAllModifiers && keys.includes(keyPressed);
};

const matchingShortcuts = (
    shortcuts: List<TypedMap<KeyboardShortcut>>,
    group: string,
    event: KeyboardEvent,
) =>
    shortcuts.filter(
        (s) =>
            s.get('group') === group &&
            checkShortcutMatch(event, s.get('value')) &&
            s.get('enabled'),
    );

const cmdOrCtrl = () => (isMacOs() ? 'CMD' : 'CTRL');

const handleOpenTransferModal: ShortcutAction = (_dispatch, event, props) => {
    event.stopPropagation();
    props.setTransferSelectionDialogOpen(true);
};

const handleBlindTransferFixed: ShortcutAction = (dispatch, _event, props) => {
    const fixedNumber = getFixedNumberFromExtension(props.extension);

    if (fixedNumber) {
        dispatch(
            transferBlindCall(
                fixedNumber,
                'keyboard shortcut ' + `B + ${cmdOrCtrl()}`,
            ),
        );
    }
};

const handleBlindTransferMobile: ShortcutAction = (dispatch, _event, props) => {
    const mobileNumber = getMobileNumberFromExtension(props.extension);

    if (mobileNumber) {
        dispatch(
            transferBlindCall(
                mobileNumber,
                'keyboard shortcut ' + `B + ${cmdOrCtrl()}`,
            ),
        );
    }
};

const handleAttendedTransferFixed: ShortcutAction = (
    dispatch,
    _event,
    props,
) => {
    const fixedNumber = getFixedNumberFromExtension(props.extension);

    if (fixedNumber) {
        dispatch(
            transferAttendedCall(
                fixedNumber,
                props.entityKey,
                'keyboard shortcut ' + `A + ${cmdOrCtrl()}`,
            ),
        );
    }
};

const handleAttendedTransferMobile: ShortcutAction = (
    dispatch,
    _event,
    props,
) => {
    const mobileNumber = getMobileNumberFromExtension(props.extension);

    if (mobileNumber) {
        dispatch(
            transferAttendedCall(
                mobileNumber,
                props.entityKey,
                'keyboard shortcut ' + `A + ${cmdOrCtrl()}`,
            ),
        );
    }
};

const actions: Record<string, ShortcutAction> = {
    '1': handleAttendedTransferFixed,
    '2': handleAttendedTransferMobile,
    '3': handleBlindTransferFixed,
    '4': handleBlindTransferMobile,
    '5': handleOpenTransferModal,
};

// These read the store when the key is pressed, not when the caller rendered.
const runShortcutsOfGroup =
    (group: string, event: KeyboardEvent, props: Props) =>
    (dispatch: AppDispatch, getState: () => State) => {
        const state = getState();
        const actionProps: ActionProps = {
            ...props,
            extension: selectExtension(state, props.entityKey as ExtensionKey),
        };

        matchingShortcuts(getKeyboardShortcuts(state), group, event).forEach(
            (shortcut) => {
                actions[shortcut.get('keyboardShortcutEntityId')](
                    dispatch,
                    event,
                    actionProps,
                );
            },
        );
    };

const isShortcutOfGroup =
    (group: string, event: KeyboardEvent) =>
    (_dispatch: AppDispatch, getState: () => State): boolean =>
        matchingShortcuts(getKeyboardShortcuts(getState()), group, event).size >
        0;

const useKeyboardShortcuts = () => {
    const dispatch = useAppDispatch();

    const callActionByGroup = useCallback(
        (group: string, event: KeyboardEvent, props: Props) => {
            dispatch(runShortcutsOfGroup(group, event, props));
        },
        [dispatch],
    );

    const callSearchShortcut = useCallback(
        (group: string, event: KeyboardEvent): boolean =>
            dispatch(isShortcutOfGroup(group, event)),
        [dispatch],
    );

    return { callActionByGroup, callSearchShortcut };
};
export default useKeyboardShortcuts;
